/* ==========================================================================
   Clapstore — mobile sticky add-to-cart.

   Scope of this file: visibility, and keeping the bar's displayed state honest.
   It deliberately does NOT add to cart. The bar's button is a native submit
   button bound to the real product form through the HTML `form` attribute, so
   Dawn's <product-form> handles the add exactly as it does for the main button —
   same variant, same quantity, same cart drawer, same error handling. If this
   script never runs, the bar simply stays hidden and nothing breaks.

   Three rules borrowed from cs-smooth.js and kept to the letter:
     1. Only transform and opacity are animated. The show/hide is a class on a
        translated fixed element; no layout, no paint.
     2. Every scroll listener is passive and rAF-throttled. IntersectionObserver
        is used where available so there is usually no scroll listener at all.
     3. Everything degrades to a no-op rather than to a broken control.
   ========================================================================== */
(function () {
  'use strict';

  var VARIANT_CHANGE = 'variant-change'; // PUB_SUB_EVENTS.variantChange

  /* Money strings come from Liquid's `money` filter, which may emit HTML
     entities (&#8377; for the rupee sign on some money formats). Decode once,
     through a detached <textarea> — its content is parsed as raw text, so no
     markup and no script can be introduced by this. */
  var decoder = document.createElement('textarea');
  function decodeEntities(value) {
    if (!value || value.indexOf('&') === -1) return value || '';
    decoder.innerHTML = value;
    return decoder.value;
  }

  function StickyAtc(root) {
    this.root = root;
    this.btn = root.querySelector('[data-cs-atc-btn]');
    this.label = root.querySelector('[data-cs-atc-label]');
    this.now = root.querySelector('[data-cs-atc-now]');
    this.was = root.querySelector('[data-cs-atc-was]');
    this.wasValue = root.querySelector('[data-cs-atc-was-value]');
    this.thumb = root.querySelector('[data-cs-atc-thumb]');
    this.title = root.querySelector('[data-cs-atc-title]');
    this.baseTitle = this.title ? this.title.textContent.trim() : '';
    this.variants = this.readVariants();
    this.unsubscribe = null;

    this.form = this.findForm();

    // No product form on the page — nothing safe to submit. Stand down.
    if (!this.form || !this.btn) return;

    if (!this.btn.getAttribute('form') && this.form.id) {
      this.btn.setAttribute('form', this.form.id);
    }

    this.mainButton =
      (root.dataset.watch && document.querySelector(root.dataset.watch)) ||
      this.form.querySelector('[type="submit"]');

    this.watchTarget =
      this.mainButton ||
      this.form.querySelector('.product-form__buttons') ||
      this.form;

    this.variantInput = this.form.querySelector('input[name="id"]');

    this.past = false;      // the real buy button has gone off the top
    this.atFooter = false;  // the footer is on screen
    this.observeBuyBox();
    this.observeFooter();
    this.bindInfo();
    this.bindVariantChanges();
    this.sync();

    root.dataset.csStickyAtcReady = '1';
  }

  /* ---- wiring ---------------------------------------------------------- */

  StickyAtc.prototype.findForm = function () {
    var byId = this.root.dataset.formId && document.getElementById(this.root.dataset.formId);
    if (byId) return byId;
    // Fall back to the first real add-to-cart form on the page.
    return document.querySelector('product-form form[data-type="add-to-cart-form"]');
  };

  StickyAtc.prototype.readVariants = function () {
    var node = this.root.querySelector('[data-cs-atc-variants]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  };

  /* ---- visibility ------------------------------------------------------ */

  /* The bar appears only once the real buy button has left the viewport
     upwards. Coming back down past it hides the bar again, so the two are never
     on screen at the same time competing for the same tap. */
  /* Clarity, 27 Oct 2026: the 77px bar sat over the end of every PDP (footer
     accordions, Back to top, related products) and those taps landed on it.
     So it also steps aside while the footer is on screen, and while it shows
     the page gets bottom padding equal to its measured height (safe area
     included), so nothing can be stuck underneath it. */
  StickyAtc.prototype.setVisible = function (visible) {
    if (typeof visible === 'boolean') this.past = visible;
    var show = this.past && !this.atFooter;
    this.root.classList.toggle('is-visible', show);
    var h = show ? Math.ceil(this.root.getBoundingClientRect().height) : 0;
    /* Phones only show the bar; on desktop it is display:none and measures 0. */
    document.body.style.paddingBottom = h ? h + 'px' : '';
  };

  StickyAtc.prototype.observeFooter = function () {
    var self = this;
    var foot = document.querySelector('.shopify-section-group-footer-group, footer');
    if (!foot || !('IntersectionObserver' in window)) return;
    this.footIo = new IntersectionObserver(function (entries) {
      self.atFooter = entries[entries.length - 1].isIntersecting;
      /* Re-read the buy box too: a jump (anchor link, fast fling) can carry it
         from below the screen to above it without one IO callback. */
      self.setVisible(self.watchTarget.getBoundingClientRect().bottom < 0);
    }, { threshold: 0 });
    this.footIo.observe(foot);
  };

  /* The bar's photo and title read as "the product": a tap takes the shopper
     back up to it (gallery, price, the real buy box). Keyboard-operable too. */
  StickyAtc.prototype.bindInfo = function () {
    var self = this;
    var info = this.root.querySelector('.cs-stickyatc__info');
    var targets = [info, this.thumb].filter(Boolean);
    function up(e) {
      if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      var sec = self.form.closest('.shopify-section') || document.body;
      var top = sec.getBoundingClientRect().top + window.pageYOffset;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: top < 400 ? 0 : top, behavior: reduce ? 'auto' : 'smooth' });
    }
    targets.forEach(function (el) {
      el.classList.add('cs-stickyatc__up');
      el.addEventListener('click', up);
    });
    if (info) {
      info.setAttribute('role', 'button');
      info.setAttribute('tabindex', '0');
      info.setAttribute('aria-label', 'Back to the top of the product');
      info.addEventListener('keydown', up);
    }
  };

  StickyAtc.prototype.observeBuyBox = function () {
    var self = this;

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        function (entries) {
          var entry = entries[entries.length - 1];
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          self.setVisible(scrolledPast);
        },
        { threshold: 0, rootMargin: '0px' }
      );
      this.io.observe(this.watchTarget);
      return;
    }

    // Fallback for browsers without IntersectionObserver.
    var ticking = false;
    this.onScroll = function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var rect = self.watchTarget.getBoundingClientRect();
        self.setVisible(rect.bottom < 0);
        ticking = false;
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  };

  /* ---- variant sync ----------------------------------------------------- */

  /* Two independent hooks, because Dawn gives us two and either one alone has a
     failure mode:
       1. the `variant-change` pub/sub event published by <product-info> after it
          re-renders — carries the full variant object;
       2. a bubbling `change` on the form's hidden input[name="id"], dispatched
          by ProductInfo.updateVariantInputs — survives even if pubsub.js has not
          loaded, and is also what fires on a section re-render.
     Both funnel into the same apply(), which is idempotent. */
  StickyAtc.prototype.bindVariantChanges = function () {
    var self = this;

    if (typeof subscribe === 'function') {
      this.unsubscribe = subscribe(VARIANT_CHANGE, function (event) {
        var data = event && event.data;
        if (!data) return;
        if (self.form.id && data.sectionId && self.form.id.indexOf(data.sectionId) === -1) return;
        self.apply(data.variant ? String(data.variant.id) : null);
      });
    }

    if (this.variantInput) {
      this.variantInput.addEventListener('change', function () {
        self.apply(self.variantInput.value || null);
      });
    }

    // Quantity rules and inventory can flip the main button without a variant
    // change; mirror it whenever Dawn touches the attribute.
    if (this.mainButton && 'MutationObserver' in window) {
      this.buttonObserver = new MutationObserver(function () {
        self.mirrorAvailability();
      });
      this.buttonObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
      });
    }
  };

  StickyAtc.prototype.sync = function () {
    this.apply(this.variantInput ? this.variantInput.value : null);
  };

  StickyAtc.prototype.apply = function (variantId) {
    var variant = this.variants && variantId ? this.variants[variantId] : null;

    if (variant) {
      if (this.now) this.now.textContent = decodeEntities(variant.price);

      if (this.was && this.wasValue) {
        if (variant.was) {
          this.wasValue.textContent = decodeEntities(variant.was);
          this.was.removeAttribute('hidden');
        } else {
          this.wasValue.textContent = '';
          this.was.setAttribute('hidden', '');
        }
      }

      if (this.thumb && variant.thumb && this.thumb.getAttribute('src') !== variant.thumb) {
        this.thumb.removeAttribute('srcset');
        this.thumb.src = variant.thumb;
      }

      if (this.title) {
        this.title.textContent = variant.title
          ? this.baseTitle + ' · ' + variant.title
          : this.baseTitle;
      }
    }

    this.mirrorAvailability(variant);
  };

  /* The bar must never offer to add something the main form would refuse.
     A variant marked unavailable in the lookup table, a missing variant id, or a
     disabled main button all disable the bar's button and relabel it. */
  StickyAtc.prototype.mirrorAvailability = function (variant) {
    if (!this.btn) return;

    var d = this.root.dataset;
    var hasVariant = !!(this.variantInput && this.variantInput.value);
    var mainDisabled =
      !!this.mainButton &&
      (this.mainButton.hasAttribute('disabled') ||
        this.mainButton.getAttribute('aria-disabled') === 'true');

    var soldOut = variant ? variant.available === false : false;
    var disabled = !hasVariant || soldOut || mainDisabled;

    this.btn.disabled = disabled;

    /* THE LABEL FOLLOWS THE REASON, NOT THE DISABLED FLAG.

       This used to read `else if (disabled)`, which said "Sold out" for every
       reason the button could be off — including mainDisabled, which goes true
       for a moment each time the main Add to Cart is busy handling a click.
       So tapping Add to Cart relabelled the sticky bar to SOLD OUT on a product
       with inventory_policy: continue, which can never actually sell out, and
       it stayed there. A shopper reads that as "gone" and leaves.

       Only genuine unavailability is allowed to say "Sold out" now. A busy main
       button still disables the bar's button — it must never offer to add
       something the main form would refuse — but the wording is left alone. */
    if (this.label) {
      var text = d.labelAdd;
      if (!hasVariant) text = d.labelUnavailable;
      else if (soldOut) text = d.labelSoldOut;
      if (this.label.textContent.trim() !== text) this.label.textContent = text;
    }
  };

  StickyAtc.prototype.destroy = function () {
    if (this.io) this.io.disconnect();
    if (this.footIo) this.footIo.disconnect();
    document.body.style.paddingBottom = '';
    if (this.buttonObserver) this.buttonObserver.disconnect();
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.unsubscribe) this.unsubscribe();
  };

  /* ---- boot ------------------------------------------------------------- */

  var instances = [];

  function boot() {
    document.querySelectorAll('[data-cs-sticky-atc]').forEach(function (root) {
      if (root.dataset.csStickyAtcReady) return;
      instances.push(new StickyAtc(root));
    });
  }

  function reboot() {
    instances.forEach(function (instance) {
      if (instance.destroy) instance.destroy();
    });
    instances = [];
    document.querySelectorAll('[data-cs-sticky-atc]').forEach(function (root) {
      delete root.dataset.csStickyAtcReady;
    });
    boot();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor: sections are swapped out from under us.
  document.addEventListener('shopify:section:load', reboot);
  document.addEventListener('shopify:section:unload', reboot);
})();
